import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Pause, Play, Lock } from 'lucide-react';
import { PORTFOLIO } from '@/constants/testIds';
import { CaseStudyThumb } from './CaseStudyThumb';
import { useCaseStudyVisibility } from '@/hooks/useCaseStudyVisibility';
import PortraitCard from './PortraitCard';

const PORTRAIT_SRC = '/avatars/hero.webp';

const spotlight = [
    { slug: 'homepage-redesign', title: 'Engagedly Homepage Redesign', category: 'UX Design', image: 'https://framerusercontent.com/images/wv3HYHKNQzH1y5keUPJVlvQgt0.png' },
    { slug: 'my-communication', title: 'My Communications', category: 'UX Design', image: '/api/files/95dfc24b-81c2-4073-b135-4b63cb604bd8' },
    { slug: 'cloudhub-enterprise-redesign', title: 'CloudHub Enterprise Redesign', category: 'Enterprise SaaS', image: '/api/files/150768d7-29f4-44c7-b1f7-0eb28d06975d' },
    { slug: 'symptom-tracking', title: 'Symptom Tracking', category: 'Mobile App', image: 'https://himanshutanty.com/wp-content/uploads/2025/02/Cover-3-330x260.jpg' },
    { slug: 'travel-app', title: 'Travel App For Visitors', category: 'Mobile App', image: 'https://himanshutanty.com/wp-content/uploads/2024/10/port-4-330x260.avif' },
    { slug: 'graphic-designs', title: 'Graphic Designs', category: 'Graphic Design', image: 'https://himanshutanty.com/wp-content/uploads/2025/02/image-3-2-330x260.png' },
    { slug: 'portal-for-site-contractors', title: 'Portal For Site Contractors', category: 'UX Design', image: 'https://himanshutanty.com/wp-content/uploads/2024/10/port-5-330x260.avif' },
    { slug: 'web-template', title: 'Individual Development Plan', category: 'UX Design', image: 'https://himanshutanty.com/wp-content/uploads/2024/10/port-3-330x260.avif' },
];

const Cell = ({ children, className = '', tone = 'default', testId }) => {
    const bg =
        tone === 'accent'
            ? 'bg-[var(--accent)] text-[var(--accent-ink)] border-[var(--accent)]'
            : tone === 'ink'
              ? 'bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]'
              : 'bg-[var(--surface)] text-[var(--ink)] border-[var(--line)] hover:border-[var(--line-2)]';
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid={testId}
            className={`relative rounded-[22px] border overflow-hidden transition-colors duration-500 ${bg} ${className}`}
        >
            {children}
        </motion.div>
    );
};

const Label = ({ label, tone }) => (
    <div
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 font-mono-brand text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.16em] sm:tracking-[0.2em] ${
            tone === 'light' ? 'text-current opacity-70' : 'text-[var(--ink-3)]'
        }`}
    >
        <span className="w-4 sm:w-6 h-[1px] bg-current opacity-40" />
        <span className="whitespace-nowrap">{label}</span>
    </div>
);

// Auto-rotating "Now Featuring" spotlight cell — fills the right side of the bento
const SpotlightCell = () => {
    const [i, setI] = useState(0);
    const [paused, setPaused] = useState(false);
    // Filter out any case study an admin has toggled off in /admin. If the
    // whole list becomes empty (edge case), fall back to the full spotlight
    // rather than rendering an empty tile.
    const { isVisible } = useCaseStudyVisibility();
    const list = useMemo(() => {
        const filtered = spotlight.filter((s) => isVisible(s.slug));
        return filtered.length > 0 ? filtered : spotlight;
    }, [isVisible]);

    useEffect(() => {
        if (paused) return;
        const t = setInterval(() => {
            setI((n) => (n + 1) % list.length);
        }, 3500);
        return () => clearInterval(t);
    }, [paused, list.length]);

    const active = list[Math.min(i, list.length - 1)];

    return (
        <Cell
            className="col-span-12 md:col-span-4 p-0 overflow-hidden"
            testId="hero-spotlight"
        >
            <div
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                className="flex h-full flex-col min-h-[220px]"
            >
                {/* Image row — compact 16:10 poster at the top of the tile */}
                <Link
                    to={`/work/${active.slug}`}
                    data-cursor="hover"
                    data-testid={`hero-spotlight-image-${active.slug}`}
                    aria-label={`Open ${active.title} case study`}
                    className="group relative w-full shrink-0 h-[140px] md:h-[150px] overflow-hidden bg-[var(--surface-2)] block"
                >
                    <AnimatePresence mode="popLayout">
                        <motion.div
                            key={active.slug}
                            initial={{ opacity: 0, scale: 1.06 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1.02 }}
                            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                            className="absolute inset-0 w-full h-full transition-transform duration-500 group-hover:scale-[1.03]"
                        >
                            <CaseStudyThumb slug={active.slug} showMeta={false} className="w-full h-full" />
                        </motion.div>
                    </AnimatePresence>
                    <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                    <div className="absolute top-3 left-3">
                        <span className="chip !bg-black/50 !text-white !border-white/15 backdrop-blur-md !py-1 !text-[9.5px]">
                            <Lock size={9} className="text-white/80" />
                            {active.category}
                        </span>
                    </div>
                    <div className="absolute bottom-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 pointer-events-none">
                        <ArrowUpRight size={13} />
                    </div>
                </Link>

                {/* Caption row — tight, single-line title with a play/pause */}
                <div className="flex-1 p-4 md:p-5 flex flex-col justify-between gap-3">
                    <div className="flex items-center justify-between gap-2">
                        <Label n="06" label="Now featuring" />
                        <div className="flex items-center gap-2">
                            <span className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] tabular-nums">
                                {String(i + 1).padStart(2, '0')}/{String(list.length).padStart(2, '0')}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPaused((p) => !p)}
                                aria-label={paused ? 'Play' : 'Pause'}
                                data-cursor="hover"
                                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:bg-[var(--surface-2)] transition-colors"
                            >
                                {paused ? <Play size={11} /> : <Pause size={11} />}
                            </button>
                        </div>
                    </div>

                    <AnimatePresence mode="popLayout">
                        <motion.div
                            key={active.slug}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.35 }}
                            className="min-h-[38px] flex items-center"
                        >
                            <Link
                                to={`/work/${active.slug}`}
                                data-cursor="hover"
                                className="font-display-italic text-xl md:text-[22px] leading-[1.15] text-[var(--ink)] hover:text-[var(--accent)] transition-colors line-clamp-2"
                            >
                                {active.title}
                            </Link>
                        </motion.div>
                    </AnimatePresence>

                    {/* Filmstrip progress bar — condensed to a single horizontal
                         row of tiny segments, matching Signal / Expertise height. */}
                    <div className="flex items-center gap-1">
                        {list.map((s, idx) => (
                            <button
                                key={s.slug}
                                type="button"
                                onClick={() => setI(idx)}
                                aria-label={`Show ${s.title}`}
                                data-cursor="hover"
                                className={`relative flex-1 h-[3px] rounded-full transition-all duration-500 overflow-hidden ${
                                    idx === i
                                        ? 'bg-[var(--line-2)]'
                                        : idx < i
                                            ? 'bg-[var(--accent)] opacity-60'
                                            : 'bg-[var(--line)] hover:bg-[var(--line-2)]'
                                }`}
                            >
                                {idx === i && !paused && (
                                    <motion.span
                                        key={`bar-${i}`}
                                        initial={{ width: '0%' }}
                                        animate={{ width: '100%' }}
                                        transition={{ duration: 3.5, ease: 'linear' }}
                                        className="absolute inset-y-0 left-0 rounded-full bg-[var(--accent)]"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </Cell>
    );
};

const lineParent = {
    hidden: {},
    show: { transition: { staggerChildren: 0.11, delayChildren: 0.15 } },
};
const lineChild = {
    hidden: { y: '115%' },
    show: { y: 0, transition: { duration: 0.85, ease: [0.2, 0.8, 0.2, 1] } },
};
const RevealLine = ({ children, className = '' }) => (
    <span className="block overflow-hidden">
        <motion.span variants={lineChild} className={`block ${className}`}>
            {children}
        </motion.span>
    </span>
);

// Interactive hero lead card — cursor spotlight, staggered line reveal,
// pulsing accent dot, glow-on-hover accent words, rotating proof chip.
const HeroLeadCard = () => {
    const [pos, setPos] = useState({ x: 42, y: 38 });
    const [hovering, setHovering] = useState(false);

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({
            x: ((e.clientX - r.left) / r.width) * 100,
            y: ((e.clientY - r.top) / r.height) * 100,
        });
    };

    return (
        <Cell className="col-span-12 md:col-span-8 md:row-span-3 p-6 sm:p-8 md:p-10 flex flex-col justify-between gap-6 md:gap-8 min-h-[480px]">
            <div
                onMouseMove={onMove}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                className="relative flex flex-col justify-between gap-6 md:gap-8 h-full"
            >
                {/* Cursor-following lime spotlight */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute -inset-6 md:-inset-10 z-0 transition-opacity duration-500"
                    style={{
                        opacity: hovering ? 1 : 0,
                        background: `radial-gradient(460px circle at ${pos.x}% ${pos.y}%, color-mix(in srgb, var(--accent) 16%, transparent), transparent 60%)`,
                    }}
                />

                <div className="relative z-10 flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Label label="Senior Product Designer" />
                        <span className="chip">He / Him</span>
                    </div>
                    <span
                        data-testid="hero-location-chip"
                        className="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] font-mono-brand text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[var(--ink-2)]"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot shrink-0" />
                        <span className="whitespace-nowrap">Bengaluru, India</span>
                    </span>
                </div>

                <div className="relative z-10">
                    <motion.h1
                        data-testid={PORTFOLIO.heroHeadline}
                        variants={lineParent}
                        initial="hidden"
                        animate="show"
                        className="font-display leading-[1] text-[10vw] md:text-[4.4vw] text-[var(--ink)]"
                    >
                        <RevealLine>
                            Experiences that
                        </RevealLine>
                        <RevealLine>
                            <span className="font-display-italic text-[var(--accent)] inline-block transition-[transform,text-shadow] duration-300 hover:-translate-y-[2px] hover:[text-shadow:0_0_28px_var(--accent)]">
                                feel simple
                            </span>
                        </RevealLine>
                        <RevealLine className="text-[var(--ink-2)]">
                            even when they <span className="text-[var(--ink)]">aren&rsquo;t</span>
                            <motion.span
                                aria-hidden
                                className="inline-block text-[var(--accent)]"
                                animate={{ scale: [1, 1.4, 1], opacity: [0.85, 1, 0.85] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                            >
                                .
                            </motion.span>
                        </RevealLine>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.9 }}
                        data-testid={PORTFOLIO.heroSubtitle}
                        className="mt-6 max-w-xl font-sans-brand text-[14px] md:text-[15px] leading-[1.6] text-[var(--ink-2)]"
                    >
                        I design complete experiences, from research and user
                        flows to polished, scalable interfaces, turning complex
                        problems into products that feel{' '}
                        <span className="text-[var(--ink)]">effortless</span>.
                    </motion.p>
                </div>

                <div className="relative z-10 mt-8 md:mt-10 flex items-center gap-3 flex-wrap">
                    <a
                        href="#work"
                        data-cursor="hover"
                        className="group inline-flex items-center gap-2 pl-5 pr-2 py-2 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                    >
                        See projects
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--accent-ink)] text-[var(--accent)] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                            <ArrowUpRight size={14} />
                        </span>
                    </a>
                    <a
                        href="#contact"
                        data-cursor="hover"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[var(--line-2)] text-sm font-medium text-[var(--ink)] hover:bg-white/5 transition-colors"
                    >
                        Say hi
                    </a>
                </div>

                <div className="relative z-10 mt-6 flex items-center gap-2.5 flex-wrap font-mono-brand text-[10.5px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                    <span className="w-6 h-[1px] bg-[var(--line-2)]" />
                    <span>A decade of design · since 2012</span>
                    <Link to="/about#career" data-cursor="hover" className="text-[var(--accent)] hover:opacity-80 transition-opacity whitespace-nowrap">Timeline ↗</Link>
                </div>
            </div>
        </Cell>
    );
};

const BentoHero = () => {
    return (
        <section
            id="top"
            data-testid={PORTFOLIO.heroSection}
            className="relative pt-24 md:pt-28 pb-12 md:pb-16 px-4 md:px-6"
        >
            <div className="mx-auto max-w-[1400px] grid grid-cols-12 auto-rows-[minmax(120px,auto)] gap-3 md:gap-4">
                {/* Headline / lead card */}
                <HeroLeadCard />

                {/* Portrait */}
                <PortraitCard
                    src={PORTRAIT_SRC}
                    name="Himanshu Kumar Tanty"
                    tagline="Enterprise UX, AI & SaaS"
                    className="col-span-6 md:col-span-4 md:row-span-2"
                    minHeight={380}
                    to="/about"
                    ctaLabel="Read my story"
                />

                {/* Rotating featured work */}
                <SpotlightCell />
            </div>
        </section>
    );
};

export default BentoHero;
